// Shared nav rendering used by both the desktop rail (Sidebar) and the
// mobile drawer (MobileSidebar). Click handler is provided externally so
// the drawer can close itself on navigation.

import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CircleDot } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/lib/routes';

export function SidebarHeader() {
  return (
    <div className="px-5 pt-5 pb-3 flex items-center gap-2">
      <div className="size-7 rounded-md bg-gradient-to-br from-indigo-500 to-cyan-500 grid place-items-center text-zinc-950 font-bold">
        T
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-zinc-100">T212</span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-400">Workstation</span>
      </div>
    </div>
  );
}

export function SidebarLinks({ onNavigate }: { onNavigate?: () => void }) {
  const state = useRouterState();
  const path = state.location.pathname;
  return (
    <nav className="flex flex-col px-2 py-2 gap-0.5">
      {ROUTES.map((item) => {
        const Icon = item.icon;
        const active = path === item.to || (item.to !== '/' && path.startsWith(item.to));
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors',
              active
                ? 'bg-indigo-500/15 text-indigo-100'
                : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900',
            )}
          >
            <Icon className="size-4" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function SidebarStatus() {
  const status = useQuery({
    queryKey: ['status'],
    queryFn: api.status,
    refetchInterval: 30_000,
  });
  return (
    <div className="px-3 pb-4 pt-3 border-t border-zinc-800/80 mx-2">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <CircleDot
          className={cn(
            'size-3',
            status.data?.t212 === 'connected'
              ? 'text-emerald-400'
              : status.data?.mode === 'demo'
                ? 'text-amber-400'
                : 'text-rose-400',
          )}
        />
        <div className="flex flex-col leading-tight">
          <span className="text-[11px] text-zinc-300">
            {status.data?.mode === 'demo' ? 'Demo mode' : 'Live mode'}
          </span>
          <span className="text-[10px] text-zinc-400">
            {status.data?.t212 === 'connected'
              ? 'T212 connected'
              : status.data?.mode === 'demo'
                ? 'Fixture data'
                : 'No T212 keys'}
          </span>
        </div>
      </div>
    </div>
  );
}
